import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useFormikContext } from 'formik'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { useSignup } from '@app/context/SignupContext'

import { FormValuesLegalDocs } from '@pages/Signup/Pages/PersonalDetails/PersonalDetailsDocumentsFactory'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Title2 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Radio } from '@ui/components/Radio'

import { isProductMarketDataClassificationSet } from '@utils/AccountAccess/accountProductStatuses'
import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { getMarketDataClassificationsName } from '@model/MarketDataClassificationsType'
import { NameDto } from '@model/NameDto'

import { createFormRadioField } from '@global/formField/RadioGroupField'

import { useScrollToTop } from '@hooks/useScrollToTop'
import { useLocallyPersistedState } from '@hooks/useStorage'

import { Loading } from '@shared/components/Loading/Loading'

import { DocumentTemplate } from '../../Signup/Components/DocumentTemplate'
import { LegalDocumentsValues } from './LegalDocumentsForm'

import styles from './ReadDocumentMarketDataPage.module.scss'

const FormRadioGroupField = createFormRadioField<LegalDocumentsValues>()

const useDocumentMarketDataClassifications = () => {
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const { data: marketDataClassifications = [], isLoading } = useQuery({
    queryKey: queryKeys.productRegistration.marketDataClassifications(locale),
    queryFn: () => apiClient.getMarketDataClassifications(locale),
  })

  return { marketDataClassifications, isLoading }
}

export const useDocumentMarketDataClassificationsMutate = () => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { account } = useAccountReadContext()
  const { refreshAccount } = useAccountWriteContext()
  const { product } = useProductReadContext()
  const locale = useSessionLanguage()
  const [isLoading, setLoading] = useState(false)
  const { marketDataClassificationId } = useDocumentMarketDataIdState()

  const mutate = async (values: FormValuesLegalDocs, callback?: () => void) => {
    try {
      setLoading(true)
      const documentMarketDataClassificationId =
        values.readDocumentMarketData?.marketDataClassificationId || marketDataClassificationId
      if (
        documentMarketDataClassificationId &&
        !isProductMarketDataClassificationSet(account, product)
      ) {
        await apiClient.updateMarketDataClassifications(documentMarketDataClassificationId)
        await refreshAccount(locale)
        await queryClient.invalidateQueries({ queryKey: queryKeys.account.profile() })
        callback?.()
      }
    } catch (e: unknown) {
      if (isMarketDataClassificationAlreadyAcceptedError(e)) {
        callback?.()
      }
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  const isMarketDataClassificationAlreadyAcceptedError = (e: unknown) => {
    const MarketDataAlreadySet = 'market_data_classification'
    const error = e as ResponseError
    const code = error.response?.response?.data?.code

    return code === MarketDataAlreadySet
  }

  return {
    mutate,
    isLoading,
  }
}

export const useDocumentMarketDataIdState = () => {
  const { account } = useAccountReadContext()
  const [urlParams] = useSearchParams()
  const { signupData } = useSignup()

  const token = urlParams.get('token') || signupData.lead?.token || ''
  const hash = urlParams.get('hash') || ''
  const documentMarketDataId = account?.id || token || hash || ''

  const MarketDataClassificationKey = `DocumentMarketDataClassificationIdKey${documentMarketDataId}`
  const [marketDataClassificationId, setMarketDataClassificationId] = useLocallyPersistedState(
    MarketDataClassificationKey,
    0
  )

  return { marketDataClassificationId, setMarketDataClassificationId }
}

interface ReadDocumentMarketDataProps {
  onAccept(values: LegalDocumentsValues): Promise<void>
}

export const ReadDocumentMarketDataPage: React.FC<ReadDocumentMarketDataProps> = (props) => {
  useScrollToTop()
  const { onAccept } = props

  const { t } = useTranslation()

  const { values, setFieldValue } = useFormikContext<LegalDocumentsValues>()
  const { marketDataClassifications = [], isLoading } = useDocumentMarketDataClassifications()

  const handleMarketDataAgreementAccept = async () => {
    await onAccept(values)
  }

  const handleMarketDataAgreementCancel = () => {
    setFieldValue('readDocumentMarketData', {
      marketDataClassificationId: 0,
      isDocumentMarketDataRead: false,
      isDocumentMarketDataAccepted: false,
    })
  }

  return (
    <DocumentTemplate
      title={t('Sign up.Exchange Market Data Client Classification')}
      goBack={handleMarketDataAgreementCancel}
    >
      <Loading isLoading={isLoading} showLoadingIcon>
        <div className='is-flex is-flex-direction-row mt-4 pl-4 pr-4'>
          <div className='is-align-items-start is-flex-grow-1'>
            <DocumentMarketDataContent />
            <div className='mb-5'>
              <div className='mb-4'>
                <FormRadioGroupField
                  name={'readDocumentMarketData.marketDataClassificationId'}
                  value={values.readDocumentMarketData?.marketDataClassificationId}
                  required
                >
                  {marketDataClassifications.map((x) => (
                    <Radio
                      name={'readDocumentMarketData.marketDataClassificationId'}
                      value={x.id}
                      checked={isMarketDataClassificationItemSelected(x, values)}
                      key={x.id}
                    >
                      {t(getMarketDataClassificationsName(x.id))}
                    </Radio>
                  ))}
                </FormRadioGroupField>
              </div>
              <div className={styles.agreeButtons}>
                <Button.Secondary size='lg' fullWidth onClick={handleMarketDataAgreementCancel}>
                  {t('Cancel')}
                </Button.Secondary>
                <Button.Primary
                  size='lg'
                  fullWidth
                  disabled={!isMarketDataClassificationSelected(values)}
                  onClick={handleMarketDataAgreementAccept}
                >
                  {t('Accept')}
                </Button.Primary>
              </div>
            </div>
          </div>
        </div>
      </Loading>
    </DocumentTemplate>
  )
}

const DocumentMarketDataContent = () => {
  const { t } = useTranslation()

  return (
    <div className={styles.content}>
      <div>
        <p className='mb-4'>{t('Sign up.Terms and conditions for receipt of')}</p>
        <p>{t('Sign up.These Terms & Conditions between Tickmill UK Ltd Vendor permits')}</p>
        <p className='mb-4'>
          {t('Sign up.The subscription fees are charged to your account upon approval')}
        </p>
        <p className='mb-4'>
          {t(
            'Sign up.Corporate clients are considered by the exchanges to be PROFESSIONAL INVESTORS'
          )}
        </p>
        <p className='mb-4'>
          {t(
            'Sign up.Subscriber furthermore agrees that Vendor may store and provide the exchange'
          )}
        </p>
        <p>
          {t(
            'Sign up.Based on the Subscribers qualifications, Subscribers may be charged reduced Fees for Information'
          )}
        </p>
      </div>
      <div className='mb-5'>
        <Title2 className='has-text-weight-bold'>
          {t('Sign up.A Private Investor non-professional shall mean and include either')}
        </Title2>
        <ul>
          <li>{t('Sign up.(a) An individual, natural person Subscriber(s) who, or')}</li>
          <li>
            {t(
              'Sign up.(b) certain small business entities (limited liability companies, partnerships)'
            )}
          </li>
        </ul>
      </div>
      <div className='mb-5'>
        <Title2 className='has-text-weight-bold'>
          {t('Sign up.The Private Investor (non-professional Subscriber) must')}
        </Title2>
        <ul>
          <li>{t('Sign up.(a) have an active futures trading account')}</li>
          <li>{t('Sign up.(b) be an individual, natural person or small business entity')}</li>
        </ul>
      </div>
      <div className='mb-5'>
        <Title2 className='has-text-weight-bold'>
          {t('Sign up.The Private Investor (non-professional) Subscriber must not')}
        </Title2>
        <ul>
          <li>{t('Sign up.(c) be a member (or hold or lease any type membership) of')}</li>
          <li>{t('Sign up.(d) does not operate a credit institution or')}</li>
          <li>{t('Sign up.(e) is not subject to any domestic or foreign banking')}</li>
          <li>
            {t(
              'Sign up.(f) be registered or qualified as a professional trader or investment adviser with any'
            )}
          </li>
          <li>
            {t('Sign up.(g) be acting on behalf of an institution that engages in brokerage')}
          </li>
        </ul>
      </div>
      <div className='mb-5'>
        <Title2 className='has-text-weight-bold'>
          {t('Sign up.The Non-Professional Subscriber’s Use of Information must')}
        </Title2>
        <ul className='mb-4'>
          <li>{t('Sign up.(h) be solely for the Subscriber’s personal or private use')};</li>
          <li>{t('Sign up.(i) be limited to managing the Subscriber’s own assets')}</li>
        </ul>
        <p>
          {t(
            'Sign up.The Subscriber shall notify the Vendor promptly in writing of any changes which have the effect'
          )}
        </p>
      </div>
      <div className='mb-5'>
        <span>
          <span className='has-text-weight-bold'>{t('Sign up.A Professional Investor')}:</span>
          <span>
            {' '}
            {t(
              'Sign up.Is a Subscriber who does not qualify as a Private Investor (as outlined above)'
            )}
          </span>
        </span>
      </div>
      <p className='mb-5'>
        {t('Sign up.Subscriber is please select one of the two following radio buttons')}
      </p>
    </div>
  )
}

const isMarketDataClassificationSelected = (values: LegalDocumentsValues) => {
  return values.readDocumentMarketData?.marketDataClassificationId > 0
}

const isMarketDataClassificationItemSelected = (x: NameDto, values: LegalDocumentsValues) => {
  const marketDataClassificationId = values.readDocumentMarketData?.marketDataClassificationId || ''
  return (
    Number.parseInt(x.id.toString(), 10) ===
    Number.parseInt(marketDataClassificationId?.toString(), 10)
  )
}
